import { useEffect } from "react";

const EDITABLE_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);
const STYLE_ELEMENT_ID = "quiz-content-protection-style";

const isEditableTarget = (target) => {
  if (!(target instanceof HTMLElement)) return false;
  if (EDITABLE_TAGS.has(target.tagName)) return true;
  return Boolean(target.isContentEditable || target.closest("[contenteditable='true']"));
};

const isWithinProtectedRoot = (root, target) => {
  if (!root) return false;
  if (target instanceof Node && root.contains(target)) return true;
  const active = document.activeElement;
  return active instanceof Node ? root.contains(active) : false;
};

const selectionTouchesRoot = (root) => {
  if (!root) return false;
  const selection = window.getSelection?.();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return false;

  for (let index = 0; index < selection.rangeCount; index += 1) {
    const range = selection.getRangeAt(index);
    const commonAncestor = range.commonAncestorContainer;
    if (commonAncestor instanceof Node && root.contains(commonAncestor)) {
      return true;
    }
  }

  return false;
};

const ensureProtectionStyle = () => {
  if (document.getElementById(STYLE_ELEMENT_ID)) return;

  const style = document.createElement("style");
  style.id = STYLE_ELEMENT_ID;
  style.textContent = `
    [data-quiz-protected-root="true"],
    [data-quiz-protected-root="true"] * {
      user-select: none !important;
      -webkit-user-select: none !important;
      -webkit-touch-callout: none !important;
    }

    [data-quiz-protected-root="true"] input,
    [data-quiz-protected-root="true"] textarea,
    [data-quiz-protected-root="true"] select,
    [data-quiz-protected-root="true"] [contenteditable="true"] {
      user-select: text !important;
      -webkit-user-select: text !important;
      -webkit-touch-callout: default !important;
    }
  `;
  document.head.appendChild(style);
};

export default function useQuizContentProtection(containerRef, enabled = true) {
  useEffect(() => {
    if (!enabled) return undefined;
    ensureProtectionStyle();

    const initialRoot = containerRef.current;
    if (initialRoot instanceof HTMLElement) {
      initialRoot.setAttribute("data-quiz-protected-root", "true");
    }

    const shouldBlock = (event, { allowEditable = false } = {}) => {
      const currentRoot = containerRef.current;
      if (!currentRoot) return false;

      const targetIsInside = isWithinProtectedRoot(currentRoot, event.target);
      const selectionIsInside = selectionTouchesRoot(currentRoot);
      if (!targetIsInside && !selectionIsInside) return false;

      const activeElement = document.activeElement;
      if (allowEditable && (isEditableTarget(event.target) || isEditableTarget(activeElement))) return false;
      return true;
    };

    const preventEvent = (event, options = {}) => {
      if (!shouldBlock(event, options)) return;
      event.preventDefault();
      event.stopPropagation();
      if (selectionTouchesRoot(containerRef.current)) {
        window.getSelection?.()?.removeAllRanges();
      }
    };

    const handleCopy = (event) => preventEvent(event, { allowEditable: true });
    const handleCut = (event) => preventEvent(event, { allowEditable: true });
    const handleSelectStart = (event) => preventEvent(event, { allowEditable: true });
    const handleSelectionChange = () => {
      const currentRoot = containerRef.current;
      const activeElement = document.activeElement;
      if (isEditableTarget(activeElement)) return;
      if (selectionTouchesRoot(currentRoot)) {
        window.getSelection?.()?.removeAllRanges();
      }
    };

    const handleKeyDown = (event) => {
      if (!shouldBlock(event, { allowEditable: true })) return;
      if (!(event.ctrlKey || event.metaKey)) return;

      const key = String(event.key || "").toLowerCase();
      const blockedKeys = new Set(["a", "c", "x", "p"]);
      if (!blockedKeys.has(key)) return;

      event.preventDefault();
      event.stopPropagation();
    };

    document.addEventListener("contextmenu", preventEvent, true);
    document.addEventListener("copy", handleCopy, true);
    document.addEventListener("cut", handleCut, true);
    document.addEventListener("selectstart", handleSelectStart, true);
    document.addEventListener("dragstart", preventEvent, true);
    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("selectionchange", handleSelectionChange, true);

    return () => {
      if (initialRoot instanceof HTMLElement) {
        initialRoot.removeAttribute("data-quiz-protected-root");
      }
      document.removeEventListener("contextmenu", preventEvent, true);
      document.removeEventListener("copy", handleCopy, true);
      document.removeEventListener("cut", handleCut, true);
      document.removeEventListener("selectstart", handleSelectStart, true);
      document.removeEventListener("dragstart", preventEvent, true);
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("selectionchange", handleSelectionChange, true);
    };
  }, [containerRef, enabled]);
}
